import React, { useContext } from "react";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE } from "common/constants";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { GetWorkflowQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexRow } from "../Common/__styles__/Layout";
import { Actions } from "../Common/ActionCell";
import { Icon } from "../Common/Icons/LucideIcons";
import { Tag } from "../Common/Tag";
import { ActionsProps } from "../Inputs/DropdownMenu";
import { WorkflowStepContainer } from "./__styles__/WorkflowStep";
import { useDeleteWorkflowStepModal } from "./DeleteWorkflowStepModal";
import { useInsertWorkflowStepModal } from "./InsertWorkflowStepModal";
import { useShareWorkflowModal } from "./ShareWorkflowModal";
import { useUpdateWorkflowStepStatusModal } from "./useUpdateWorkflowStepStatusModal";
import { WorkflowStepCTA } from "./WorkflowStepCTA";
import { getWorkflowStepStatusDisplay } from "./workflowUtils";

export const WorkflowStep = ({
  workflowStep,
  workflowId,
  isManualMode,
  objectId,
  objectType,
  workflow,
}: {
  workflowStep: Omit<
    NonNullable<GetWorkflowQuery["workflow"]>["workflowSteps"][number],
    "status"
  >;
  workflowId: string;
  isManualMode: boolean;
  objectId?: string;
  objectType?: OBJECT_TYPE;
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
}) => {
  const { authorized } = useContext(AuthContext);

  const statusTagInfo =
    isManualMode ?
      getWorkflowStepStatusDisplay(workflowStep.manualStatus)
    : undefined;

  const stepName = workflowStep.name;

  const actions: ActionsProps[] = [];

  const canUpdateWorkflow = authorized({
    permission: "update",
    resource: RESOURCE_NAME.WORKFLOW,
  });

  const canShareWorkflow =
    workflow.canShare &&
    authorized({ permission: "share", resource: RESOURCE_NAME.WORKFLOW });

  const [showUpdateStatusModal] = useUpdateWorkflowStepStatusModal({
    workflowStepId: workflowStep.id,
    workflowStepManualStatus: workflowStep.manualStatus,
    submissionTypeName: workflowStep.name,
    onUpdate: () => {
      // Refresh handled by refetchQueries in mutation
    },
  });

  const [showInsertWorkflowStepModal] = useInsertWorkflowStepModal({
    workflow,
    workflowStepDefinition: workflowStep.workflowStepDefinition,
    startingName: workflowStep.name,
    addAtOrderIndex: workflowStep.order + 1,
    clonedFromStepId: workflowStep.id,
  });

  const [showDeleteWorkflowStepModal] = useDeleteWorkflowStepModal({
    workflowStep,
    workflow,
  });

  const [showShareModal] = useShareWorkflowModal({
    entityType: "workflowStep",
    entityId: workflowStep.id,
    workflowId,
    entityName: workflowStep.name,
    workflowName: workflow.workflowDefinition.name,
  });

  if (canUpdateWorkflow) {
    actions.push(
      { label: "Update status", onClick: showUpdateStatusModal },
      { label: "Add step", onClick: showInsertWorkflowStepModal },
    );

    if (workflowStep.clonedFromStep) {
      actions.push({
        label: "Delete",
        onClick: showDeleteWorkflowStepModal,
      });
    }
  }

  if (canShareWorkflow) {
    actions.push({ label: "Add collaborator", onClick: showShareModal });
  }

  return (
    <WorkflowStepContainer data-testid="workflow-step">
      <FlexRow style={{ gap: spacing.s.value, alignItems: "center" }}>
        <Icon
          iconName="file-text"
          color="contentSecondary"
          size={15}
          style={{
            flexShrink: 0,
          }}
        />
        {stepName}
      </FlexRow>
      <FlexRow style={{ gap: spacing.l.value, alignItems: "center" }}>
        {workflowStep.workflowStepUser?.tag && !isManualMode && (
          <Tag styleVariant="default" data-testid="workflow-step-tag">
            {workflowStep.workflowStepUser?.tag}
          </Tag>
        )}
        {statusTagInfo && (
          <Tag
            styleVariant={statusTagInfo.variant}
            data-testid="workflow-step-status-tag"
          >
            {statusTagInfo.text}
          </Tag>
        )}
        <WorkflowStepCTA
          workflowStep={workflowStep}
          objectId={objectId}
          objectType={objectType}
          isManualMode={isManualMode}
        />
        {isManualMode && arrayHasAtLeastOneItem(actions) && (
          <>
            <Actions actions={actions} />
          </>
        )}
      </FlexRow>
    </WorkflowStepContainer>
  );
};
